import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { ListPerson } from "../schema/list";

/**
 * The rows a bulk contact-info action applies to: the one row a confirmation dialog is open for,
 * or the current selection — in both cases only rows actually on the page.
 *
 * The three bulk lookups (phone / email / company email & phone) each carried their own copy of
 * this, including a pointless `Promise.all` over a synchronous lookup. One copy, so a fix to the
 * selection rule lands in all three.
 */
export const resolveBulkContactInfoIds = (
	data: ListPerson[] | undefined,
	selectedItems: number[],
	selectedItemForConfirmation: { id: number } | null
): number[] => {
	if (!data) {
		return [];
	}

	const onPage = new Set(data.map((row) => row.id));

	if (selectedItemForConfirmation) {
		return onPage.has(selectedItemForConfirmation.id) ? [selectedItemForConfirmation.id] : [];
	}

	return selectedItems.filter((id) => onPage.has(id));
};

/** The scope a bulk contact-info request carries — identical across the three lookups. */
export interface BulkContactInfoScope {
	listId?: number;
	selectType: SelectType;
	inclusionFilterStatus?: InclusionFilterStatus[];
	priorityFilterStatus?: PriorityFilterStatus[];
	signalFilterStatus?: SignalFilterStatus[];
	assignStatus?: AssignStatus;
	filter?: Record<string, unknown>;
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: AssignStatus[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
}

export interface BulkContactInfoRequest extends BulkContactInfoScope {
	ids: number[];
}

export const buildBulkContactInfoRequest = (
	ids: number[],
	scope: BulkContactInfoScope
): BulkContactInfoRequest => ({ ids, ...scope });

export interface BulkContactInfoLookupParams {
	data: ListPerson[] | undefined;
	selectedItems: number[];
	selectedItemForConfirmation: { id: number } | null;
	scope: BulkContactInfoScope;
	message: string;
	notify: (message: string) => void;
	onSettled: () => void;
}

/**
 * Runs one bulk contact-info lookup: resolve the target rows, fire the mutation when there are
 * any, then say so and clear the confirmation. The three lookups differ only in which mutation
 * they pass in and what they say afterwards.
 *
 * A refused request rejects with the request's error, and says and clears nothing: the
 * credit-confirm dialog it was confirmed in stays open and shows why, "Not enough credits" when
 * the balance cannot pay one lookup (PREC-4263; a snackbar after the dialog closed before that,
 * PREC-4437).
 */
export const runBulkContactInfoLookup = async (
	lookup: (request: BulkContactInfoRequest) => { unwrap: () => Promise<unknown> },
	{
		data,
		selectedItems,
		selectedItemForConfirmation,
		scope,
		message,
		notify,
		onSettled,
	}: BulkContactInfoLookupParams
): Promise<void> => {
	const ids = resolveBulkContactInfoIds(data, selectedItems, selectedItemForConfirmation);

	if (ids.length > 0) {
		await lookup(buildBulkContactInfoRequest(ids, scope)).unwrap();
	}

	notify(message);
	onSettled();
};
